import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * LegendColumnHeader - Header for the legend column with expand/collapse button.
 * 
 * @param {Object} props
 * @param {boolean} props.isExpanded - Whether the legend is expanded
 * @param {Function} props.onToggle - Toggle function
 * @param {number} props.activityCount - Number of activities
 */
export const LegendColumnHeader = ({ isExpanded, onToggle, activityCount }) => {
  return (
    <th className="px-3 py-3 text-left font-semibold text-gray-700" style={{ width: isExpanded ? '280px' : '120px', minWidth: isExpanded ? '280px' : '120px' }}>
      <button
        onClick={onToggle}
        className="flex items-center gap-2 hover:text-gray-900 transition-colors w-full"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
        <span>Legend</span>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
          {activityCount}
        </span>
      </button>
    </th>
  );
};

/**
 * LegendColumnCell - Cell content for the legend column.
 * Shows activity list when expanded in the first row only, empty cells for other rows.
 * 
 * @param {Object} props
 * @param {boolean} props.isExpanded - Whether the legend is expanded
 * @param {Array} props.sortedActivities - Sorted array of activities
 * @param {boolean} props.isFirstRow - Whether this is the first data row
 * @param {number} props.rowSpan - Number of rows to span
 */
export const LegendColumnCell = ({ isExpanded, sortedActivities, isFirstRow, rowSpan }) => {
  // Only render content in the first row
  if (!isFirstRow) {
    return null;
  }

  if (!isExpanded) {
    return (
      <td className="px-2 py-3 text-gray-400 text-xs italic align-top text-center bg-white border-b border-gray-100" rowSpan={rowSpan}>
        Click to expand
      </td>
    );
  }

  if (sortedActivities.length === 0) {
    return (
      <td className="px-4 py-3 text-yellow-600 text-xs align-top bg-white border-b border-gray-100" rowSpan={rowSpan}>
        No activities
      </td>
    );
  }

  return (
    <td className="px-3 py-3 bg-white align-top border-b border-gray-100" rowSpan={rowSpan}>
      <div className="flex flex-col gap-1 max-h-[500px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
        {sortedActivities.map(({ code, name, color }) => {
          // Calculate contrast text color (simple approach)
          const getTextColor = (bgColor) => {
            const hex = bgColor.replace('#', '');
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);
            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
            return brightness > 155 ? '#1f2937' : '#ffffff';
          };

          return (
            <div
              key={code}
              className="flex items-center gap-2 hover:bg-gray-50 transition-colors py-0.5"
              title={name}
            >
              {/* Minimal Activity Pill matching table blocks */}
              <div
                className="flex-shrink-0 px-2 py-1 rounded text-xs font-medium"
                style={{
                  backgroundColor: color,
                  color: getTextColor(color),
                  minWidth: '56px',
                  textAlign: 'center',
                }}
              >
                {code}
              </div>
              
              {/* Activity Name - clean and minimal */}
              <span className="text-xs text-gray-700 truncate flex-1" title={name}>
                {name}
              </span>
            </div>
          );
        })}
      </div>
    </td>
  );
};

/**
 * useLegendColumn - Hook to manage legend state and provide sorted activities.
 * 
 * @param {Object} activityCatalog - Map of activity_code to { code, name }
 * @param {Object} colorPalette - Map of activity_code to color
 * @returns {Object} - { isExpanded, toggleExpanded, sortedActivities }
 */
export const useLegendColumn = (activityCatalog, colorPalette) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const sortedActivities = useMemo(() => {
    if (!activityCatalog || typeof activityCatalog !== 'object') {
      console.warn('⚠️ LegendColumn: activityCatalog is invalid:', activityCatalog);
      return [];
    }

    return Object.entries(activityCatalog)
      .map(([code, data]) => ({
        code,
        name: data.name || code,
        color: colorPalette?.[code] || '#94a3b8',
      }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [activityCatalog, colorPalette]);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  return { isExpanded, toggleExpanded, sortedActivities };
};
